import React, { useEffect, useRef, useState } from 'react';
import { Route, Routes, useSearchParams, useLocation } from 'react-router-dom';
import Cookies from 'universal-cookie';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { userMe, userIsLoggedIn, userAutoLogin } from '@/store/auth';
import { claimJobberTokens } from '@/store/integrations';

import Login from './Login';
import Register from './Register';
import Recovery from './Recovery';
import Billing from './Billing';
import Dashboard from './Dashboard';
import Profile from './Profile';
import AgencyDashboard from './AgencyDashboard';
import Lead from './Lead';
import Configuration from './Configuration';
import QuickActions from './QuickActions';
import Reports from './Reports';
import AnsweredCallRecap from './AnsweredCallRecap';
import Integrations from './Integrations';

import AppNotifications from '@/components/AppNotifications';
import AuthLayout from '@/components/AuthLayout';
import AppLayout from '@/components/AppLayout';
import ProtectedRoute from '@/components/ProtectedRoute';
import AppLoader from '@/components/AppLoader';
import AppBox from '@/components/AppBox';

const authRoutes = [
  { path: "/", element: <Dashboard />, protected: true },
  { path: "/billing", element: <Billing />, protected: false },
  { path: "/profile", element: <Profile />, protected: true },
  { path: "/agency", element: <AgencyDashboard />, protected: true },
  { path: "/lead/:id", element: <Lead />, protected: true },
  { path: "/configuration", element: <Configuration />, protected: true },
  { path: "/lead-actions/:id", element: <QuickActions />, protected: true },
  { path: "/reports", element: <Reports />, protected: true },
  { path: "/reports/answered-call", element: <AnsweredCallRecap />, protected: true },
  { path: "/integrations", element: <Integrations />, protected: true },
];

const guestRoutes = [
  { path: "/register/:agency?", element: <Register />, protected: false },
  { path: "/signup", element: <Register isAgencyForm />, protected: false },
  { path: "/recovery/:hash?", element: <Recovery />, protected: false },
  { path: "*", element: <Login />, protected: false },
];

const Client: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [search] = useSearchParams();
  const token = search.get('token');
  const cookies = new Cookies();
  
  const { isLoggedIn, me, status } = useAppSelector((state) => state.auth);
  const [ready, setReady] = useState(false);

  const hasClaimed = useRef(false);

  useEffect(() => {
    async function init() {
      if (token) {
        await dispatch(userAutoLogin({ token })).unwrap();
        // Remove token from URL once auto-login succeeds
        const url = new URL(window.location.href);
        url.searchParams.delete('token');
        window.history.replaceState({}, document.title, url.toString());
      } else if (cookies.get('gls-token')) {
        await dispatch(userMe()).unwrap();
      }
      setReady(true);
    }
    init();
  }, []);

  useEffect(() => {
    if (me?.id && !hasClaimed.current) {
      const queryParams = new URLSearchParams(location.search);
      const jobberOAuthKey = queryParams.get("jobber_oauth");
  
      if (jobberOAuthKey) {
        hasClaimed.current = true;
        (async () => {
          await dispatch(claimJobberTokens(jobberOAuthKey));
          await dispatch(userMe()); // now runs AFTER the tokens are saved
  
          const url = new URL(window.location.href);
          queryParams.delete("jobber_oauth");
          url.search = queryParams.toString();
          window.history.replaceState({}, document.title, url.toString());
        })();
      }
    }
  }, [location.pathname, me?.id]);

  useEffect(() => {
    // After any logout, Redux sets isLoggedIn = false but the logout reducer
    // might have restored a previous `gls-token` cookie (e.g. from nested "login-as" flow).
    // If we have a token cookie yet Redux thinks we are logged out, bootstrap the session again.
    if (ready && !isLoggedIn && !token && cookies.get('gls-token') && !me?.id && status !== 'loading') {
      dispatch(userMe());
    }
  }, [ready, isLoggedIn, token, status, me?.id]);

  // Show loading while checking authentication or if we have a token but aren't logged in yet
  // But don't show loader if we're logged in and have user data
  const shouldShowLoader = !ready || (token && !isLoggedIn && !me?.id) || (status === 'loading' && !me?.id);
  
  if (shouldShowLoader) {
    return <AppBox className="flex justify-center items-center h-screen">
      <AppLoader />
    </AppBox>;
  }

  // Dynamic Route Rendering
  const renderRoutes = (routes: any[]) => {
    return routes.map(({ path, element, protected: isProtected }) => (
      <Route
        key={path}
        path={path}
        element={isProtected ? <ProtectedRoute>{element}</ProtectedRoute> : element}
      />
    ));
  };

  return (
    <>
      {isLoggedIn && me?.id ? (
        <AppLayout>
          <Routes>{renderRoutes(authRoutes)}</Routes>
        </AppLayout>
      ) : (
        <AuthLayout>
          <Routes>{renderRoutes(guestRoutes)}</Routes>
        </AuthLayout>
      )}
      <AppNotifications />
    </>
  );
};

export default Client;